import React, { useEffect, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useOrganization } from '../../contexts/OrganizationContext';
import {
  useMobileNav,
  MobileNavSidebarVariant,
} from '../../contexts/MobileNavContext';
import ApplicationSidebar from './ApplicationSidebar';
import FindingsListSidebar from './FindingsListSidebar';
import ComplianceSidebar from './ComplianceSidebar';
import IntegrationStatusBanner from '../common/IntegrationStatusBanner';
import SampleDataBanner from '../common/SampleDataBanner';
import ScanProgressBanner from '../scans/ScanProgressBanner';
import LeadQuestionnaireModal from '../modals/LeadQuestionnaireModal';

const SidebarLayout: React.FC = () => {
  const { currentOrganization } = useOrganization();
  const location = useLocation();
  const { setSidebarVariant } = useMobileNav();

  // Determine which sidebar to show based on the current route
  const sidebarVariant = useMemo<MobileNavSidebarVariant>(() => {
    const path = location.pathname;

    // Single finding view — show the findings list so the user can page through findings.
    // Must be checked before the general application match below.
    if (path.match(/^\/repositories\/[^/]+\/findings\/[^/]+$/)) {
      return 'findings';
    }

    // Application routes (with specific app selected)
    if (path.match(/^\/repositories\/[^/]+/)) {
      return 'application';
    }

    // Compliance routes
    if (path.startsWith('/compliance')) {
      return 'compliance';
    }

    return 'none';
  }, [location.pathname]);

  // Publish the current variant so Header's mobile drawer can render it too
  useEffect(() => {
    setSidebarVariant(sidebarVariant);
    return () => setSidebarVariant('none');
  }, [sidebarVariant, setSidebarVariant]);

  const renderSidebar = () => {
    switch (sidebarVariant) {
      case 'application':
        return <ApplicationSidebar />;
      case 'findings':
        // Keep the app sidebar as a collapsed rail and show the findings list beside it.
        return (
          <>
            <ApplicationSidebar collapsed />
            <FindingsListSidebar />
          </>
        );
      case 'compliance':
        return <ComplianceSidebar />;
      case 'none':
      default:
        return null;
    }
  };

  const sidebar = renderSidebar();

  return (
    <div
      key={currentOrganization?.id}
      className="flex h-full w-full overflow-x-hidden bg-ui-background-primary"
    >
      {sidebar && <div className="hidden md:flex">{sidebar}</div>}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <SampleDataBanner />
        <ScanProgressBanner />
        <IntegrationStatusBanner />
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
      <LeadQuestionnaireModal />
    </div>
  );
};

export default SidebarLayout;
